import React, { useEffect, useRef, useState } from "react";
import { compressLogo } from "@/lib/compressLogo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Palette, Upload, Undo2, Check, Building2 } from "lucide-react";

const PRESETS = [
  { name: "Zenith Blue",   hex: "#3B82F6" },
  { name: "Ember Orange",  hex: "#F16522" },
  { name: "Forest",        hex: "#059669" },
  { name: "Royal Purple",  hex: "#7C3AED" },
  { name: "Crimson",       hex: "#DC2626" },
  { name: "Midnight",      hex: "#0F172A" },
  { name: "Amber",         hex: "#F59E0B" },
  { name: "Rose",          hex: "#E11D48" },
];

// Convert hex → HSL tuple string ("h s% l%") that Tailwind CSS vars expect.
function hexToHslString(hex) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map(c => c+c).join("") : h, 16);
  const r = ((bigint >> 16) & 255) / 255;
  const g = ((bigint >> 8) & 255) / 255;
  const b = (bigint & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hh = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      case b: hh = (r - g) / d + 4; break;
      default: break;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export default function BrandingPanel() {
  const { org, patchOrgLocal } = useAuth();
  const [logo, setLogo] = useState(org?.logo_base64 || "");
  const [color, setColor] = useState(org?.brand_color || "#3B82F6");
  const [footer, setFooter] = useState(org?.invoice_footer_note || "");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    setLogo(org?.logo_base64 || "");
    setColor(org?.brand_color || "#3B82F6");
    setFooter(org?.invoice_footer_note || "");
  }, [org?.id]);

  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    compressLogo(f).then(setLogo).catch((err) => toast.error(err.message));
    e.target.value = "";
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/organization", {
        logo_base64: logo, brand_color: color, invoice_footer_note: footer,
      });
      patchOrgLocal({ logo_base64: logo, brand_color: color, invoice_footer_note: footer });
      // Apply the accent HSL live so nav/buttons re-tint immediately
      document.documentElement.style.setProperty("--accent", hexToHslString(color));
      document.documentElement.style.setProperty("--ring", hexToHslString(color));
      toast.success("Branding saved — portal + PDFs will now carry your colours");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const clearLogo = () => setLogo("");

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="branding-panel">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-accent">Admin · Branding</div>
        <h1 className="font-heading text-2xl mt-1">Portal & PDF branding</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Upload your logo and pick an accent colour. It flows everywhere — sidebar, buttons,
          links, headings, and every PDF exported from Zenith 360.
        </p>
      </div>

      {/* LIVE PREVIEW */}
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        <div className="card-sharp p-4 space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Live preview</div>
          <div className="rounded-sm p-3 bg-[#0A0A0F] text-foreground">
            <div className="flex items-center gap-2">
              {logo
                ? <img src={logo} alt="logo" className="w-9 h-9 rounded-sm object-cover shadow" data-testid="preview-logo" />
                : <div className="w-9 h-9 rounded-sm bg-muted flex items-center justify-center"><Building2 className="w-4 h-4 text-muted-foreground" /></div>}
              <div>
                <div className="font-heading font-bold text-sm leading-none">{org?.name?.slice(0, 22) || "Your company"}</div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground/80 mt-0.5">ERP · v1.4</div>
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              {[["Dashboard", true], ["Sales", false], ["Purchases", false]].map(([l, on]) => (
                <div key={l} className={`text-xs px-2 py-1 rounded-sm flex items-center gap-2 ${on ? "bg-muted" : ""}`}>
                  <span className="w-1 h-3 rounded-sm" style={{ background: on ? color : "transparent" }} />
                  {l}
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <Button style={{ background: color }} className="rounded-sm text-foreground text-xs h-8">Primary</Button>
            <Button variant="outline" className="rounded-sm text-xs h-8" style={{ borderColor: color, color }}>Outline</Button>
            <span className="text-xs" style={{ color }}>Accent link</span>
          </div>
        </div>

        <div className="card-sharp p-5 space-y-5">
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider">Logo</Label>
            <div className="mt-2 flex items-center gap-3">
              {logo
                ? <img src={logo} alt="logo" className="w-16 h-16 rounded-sm object-cover border" />
                : <div className="w-16 h-16 border-2 border-dashed rounded-sm flex items-center justify-center text-muted-foreground text-xs">No logo</div>}
              <div className="flex flex-col gap-2">
                <input type="file" accept="image/png,image/jpeg,image/svg+xml" ref={fileRef}
                        onChange={onPickLogo} className="hidden" data-testid="branding-logo-input" />
                <Button size="sm" onClick={() => fileRef.current?.click()}
                          className="rounded-sm gap-1.5 h-8" data-testid="branding-upload-btn">
                  <Upload className="w-3.5 h-3.5" /> Upload logo (PNG/JPG/SVG · auto-resized)
                </Button>
                {logo && (
                  <Button size="sm" variant="ghost" onClick={clearLogo}
                            className="rounded-sm gap-1.5 h-7 text-xs text-muted-foreground">
                    <Undo2 className="w-3 h-3" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-wider">Accent colour</Label>
            <div className="mt-2 flex items-center gap-3">
              <div className="flex items-center gap-2">
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                         className="w-11 h-11 rounded-sm border cursor-pointer"
                         data-testid="branding-color-picker" />
                <Input value={color} onChange={(e) => setColor(e.target.value)}
                          className="rounded-sm w-32 font-mono text-sm" maxLength={7}
                          data-testid="branding-color-hex" />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map((p) => (
                  <button key={p.hex} onClick={() => setColor(p.hex)}
                            className={`w-8 h-8 rounded-sm border-2 relative ${color === p.hex ? "border-foreground" : "border-transparent"}`}
                            style={{ background: p.hex }} title={p.name}
                            data-testid={`preset-${p.name.replace(" ", "-")}`}>
                    {color === p.hex && <Check className="w-3.5 h-3.5 text-foreground absolute inset-0 m-auto" />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-wider">Invoice / PDF footer note</Label>
            <Input value={footer} onChange={(e) => setFooter(e.target.value)}
                      placeholder="Thank you for your business — Zenith Motors · GST 29XXXX · CIN L12345"
                      className="rounded-sm mt-2 font-mono text-xs" data-testid="branding-footer" />
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button onClick={save} disabled={saving}
                      style={{ background: color }}
                      className="rounded-sm text-foreground gap-1.5 h-9" data-testid="branding-save">
              <Palette className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save branding"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
